<template>
  <v-expansion-panels
    v-model="expansionPanel"
    class="household-member-card"
  >
    <v-expansion-panel value="all">
      <v-expansion-panel-title
        v-if="!hideTitle"
        @click="loadChildSubsidyApplications"
        v-slot="{ expanded }"
        :class="{ 'not-expandable': !isExpandable }"
        :focusable="isExpandable"
        :hide-actions="!isExpandable"
        :readonly="!isExpandable"
        :static="!isExpandable"
        class="v-expansion-title align-baseline household-member-card__title px-5"
      >
        <div class="pb-4 w-100">
          <v-row
            class="d-flex justify-start px-3"
            no-gutters
          >
            <v-col>
              <v-row class="align-center">
                <div class="fs-18 fw-600">
                  {{ getName(householdMember) }}
                </div>
                <span
                  v-if="householdMember.external_id"
                  class="ms-4 fs-16 fw-500 c-light-black"
                  data-testid="household-member-external-id"
                >
                  #{{ householdMember.external_id }}
                </span>
                <v-chip
                  v-if="managedSubsidyProgramEligibilityDetermination?.owner_name"
                  :color="eligibilityIconColor"
                  class="ms-13 badge-style justify-center"
                  variant="flat"
                  rounded
                >
                  <v-icon
                    color="white mr-2"
                    role="presentation"
                    aria-hidden
                  >
                    {{ eligibilityIcon }}
                  </v-icon>
                  <p class="font-weight-bold">
                    {{ projectedEligibility ? $bct('Eligible') : $bct('Not eligible') }}
                  </p>
                </v-chip>
              </v-row>
            </v-col>
          </v-row>
          <v-row v-if="!expanded">
            <v-col cols="12">
              <HouseholdMemberDetails
                :can-edit="false"
                :hide-ssn="hideSsn"
                :household-member="householdMember"
                summary
              />
            </v-col>
          </v-row>
        </div>
      </v-expansion-panel-title>
      <v-expansion-panel-text>
        <template v-if="editing || locked">
          <div class="mb-4">
            <HouseholdMemberFields
              @archive="archive"
              @cancel="cancel"
              @household-member-changed="householdMemberUpdated"
              @save="update"
              :archive-label="archiveLabel"
              :autofocus="autofocus"
              :edit-mode="true"
              :hard-lock="locked"
              :hide-ssn="hideSsn"
              :household-member-values="value"
              :processing="processing"
              :self-exists="selfExists"
              :spouse-exists="spouseExists"
            />
          </div>
        </template>
        <template v-else>
          <HouseholdMemberDetails
            @edit="$emit('edit', $event)"
            :can-edit="canEdit"
            :hide-ssn="hideSsn"
            :household-member="householdMember"
          />
        </template>

        <div v-if="childSubsidyApplicationsEnabled && childSubsidyApplications.length > 0">
          <v-divider class="my-6" />
          <div class="fs-16 fw-600 mb-4">
            {{ $bct('Child subsidy applications') }}
          </div>
          <div
            v-for="(application, applicationIndex) in childSubsidyApplications"
            :key="application.id"
            :class="[
              'd-flex align-center justify-space-between mb-3',
              {
                'bb-1 bc-very-light-grey pb-3':
                  applicationIndex < childSubsidyApplications.length - 1,
              },
            ]"
          >
            <div class="fs-14">
              {{ application.subsidy_program_name }}
            </div>
            <v-tooltip
              v-if="application.viewable === false"
              location="bottom"
            >
              <template #activator="{ props: tooltipProps }">
                <span v-bind="tooltipProps">
                  <StandardButton
                    :text="$bct('Review')"
                    color="primary"
                    size="small"
                    variant="flat"
                    disabled
                  />
                </span>
              </template>
              <span>
                {{ $bct('You do not have permission to view this application.') }}
              </span>
            </v-tooltip>
            <StandardButton
              v-else
              @click="reviewSubsidy(application.id, 'Application')"
              :text="$bct('Review')"
              color="primary"
              size="small"
              variant="flat"
            />
          </div>
        </div>

        <div v-if="enableRelatedSubsidies">
          <div v-if="managedSubsidyProgramEligibilityDetermination">
            <v-divider class="my-6" />
            <div class="fs-16 fw-600 mb-4">
              <div>{{ managedSubsidyProgramEligibilityDetermination.owner_name }}</div>
            </div>

            <v-row class="d-flex justify-between">
              <SubsidyEligibilityCardV2
                :funding-sources="
                  managedSubsidyProgramEligibilityDetermination.eligibility_determinations
                "
                :projected-eligibility="
                  managedSubsidyProgramEligibilityDetermination.projected_eligibility
                "
              />
              <div
                v-if="managedChildSubsidy"
                class="mx-10 d-flex justify-end"
              >
                <StandardButton
                  @click="
                    reviewSubsidyForProgramId(
                      managedSubsidyProgramEligibilityDetermination.owner_id,
                    )
                  "
                  :text="$bct('Review')"
                  color="primary"
                  custom-classes="me-3"
                  variant="flat"
                />
              </div>
              <div
                v-else
                class="mx-3 d-flex justify-end"
              >
                <StandardButton
                  @click="createSubsidy(managedSubsidyProgramEligibilityDetermination.owner_id)"
                  :disabled="creatingSubsidy"
                  :loading="creatingSubsidy"
                  :text="$bct('Enroll')"
                  color="primary"
                  custom-classes="me-3"
                  variant="flat"
                />
              </div>
            </v-row>
          </div>
          <div
            v-if="otherSubsidyProgramEligibilityDetermination.length > 0"
            class="fs-16 fw-600 mb-4 mt-6"
          >
            {{ $bct('Other subsidy programs') }}
          </div>
          <div
            v-for="otherResult in otherSubsidyProgramEligibilityDetermination"
            :key="otherResult.owner_id"
            class="mx-6"
          >
            <div class="fs-14 fw-600 mt-6">
              {{ otherResult.owner_name }} eligibility
              <v-row class="d-flex justify-between">
                <SubsidyEligibilityCardV2
                  :funding-sources="otherResult.eligibility_determinations"
                  :projected-eligibility="otherResult.projected_eligibility"
                />
                <div v-if="subsidyForOwnerId(otherResult.owner_id)">
                  <StandardButton
                    @click="reviewSubsidyForProgramId(otherResult.owner_id)"
                    :text="$bct('Review')"
                    color="primary"
                    custom-classes="me-3 fs-14"
                    size="small"
                    variant="flat"
                  />
                </div>
                <div v-else>
                  <StandardButton
                    @click="createSubsidy(otherResult.owner_id)"
                    :disabled="creatingSubsidy"
                    :loading="creatingSubsidy"
                    :text="$bct('Enroll')"
                    color="primary"
                    custom-classes="me-3 fs-14"
                    size="small"
                    variant="outlined"
                  />
                </div>
              </v-row>
            </div>
          </div>
        </div>
        <v-divider
          v-if="householdMemberQuestionRegister[householdMember.id]"
          class="mt-10 mb-6"
        />
        <QuestionSet
          v-if="householdMemberQuestionRegister[householdMember.id]"
          @change="handleChange"
          @change:attachments="loadAttachments()"
          @change:input="handleChange"
          :attachment-group-id="subsidy.group_id"
          :attachment-owner-id="subsidy.id"
          :attachment-owner-type="'FamilySubsidy'"
          :attachment-tags-supplements="attachmentTagsSupplements"
          :attachments="attachments"
          :color="null"
          :key-name="householdMember.id"
          :model-value="householdMember"
          :processing="processing"
          :questions="
            getHouseholdMemberQuestions(householdMember).filter((question) => question.valid)
          "
          :readonly="!canEditQuestions"
          :schema="householdMemberSchema.definition"
          class="mb-6"
          condensed
          dense
          divided
          expanded
          hide-actions
          tile
        />
        <v-row
          v-if="changed && canEditQuestions && !processing"
          class="pb-8"
        >
          <v-col class="d-flex justify-end">
            <StandardButton
              v-if="!hideSave"
              @click="update"
              :loading="processing"
              :text="$bct('Save')"
              color="primary"
              size="large"
            />
          </v-col>
        </v-row>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>

  <ConfirmDialog ref="confirmDialog" />
</template>

<script setup>
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import ConfirmDialog from '@/shared/components/ConfirmDialog.vue';
import HouseholdMemberDetails from '@/shared/components/household-members/HouseholdMemberDetails.vue';
import HouseholdMemberFields from '@/shared/components/household-members/HouseholdMemberFields.vue';
import QuestionSet from '@/shared/components/form/QuestionSet.vue';
import SubsidyEligibilityCardV2 from '@/shared/components/subsidy/SubsidyEligibilityCardV2.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import useRolePermissions from '@/shared/composables/useRolePermissions';

const eventBus = useEventBus();
const { familySubsidiesEdit } = useRolePermissions();
const router = useRouter();
const store = useStore();
const $bct = inject('translator');

const props = defineProps({
  archiveLabel: {
    type: String,
    default: 'Archive',
  },
  autofocus: {
    type: Boolean,
    default: false,
  },
  canEdit: {
    type: Boolean,
    default: true,
  },
  editing: {
    type: Boolean,
    default: false,
  },
  hideTitle: {
    type: Boolean,
    default: false,
  },
  hideSave: {
    type: Boolean,
    default: false,
  },
  hideSsn: {
    type: Boolean,
    default: false,
  },
  householdMemberApi: {
    type: Object,
    default: null,
  },
  householdMemberQuestionRegister: {
    type: Object,
    default: () => ({}),
  },
  householdMemberSchema: {
    type: Object,
    default: () => ({}),
  },
  index: {
    type: Number,
    default: null,
  },
  isExpandable: {
    type: Boolean,
    default: false,
  },
  locked: {
    type: Boolean,
    default: false,
  },
  managedSubsidyProgramId: {
    type: String,
    default: null,
  },
  outlined: {
    type: Boolean,
    default: false,
  },
  preventUpdate: {
    type: Boolean,
    default: false,
  },
  selfExists: {
    default: false,
    type: Boolean,
  },
  spouseExists: {
    default: false,
    type: Boolean,
  },
  staged: {
    default: false,
    type: Boolean,
  },
  subsidy: {
    type: Object,
    default: () => ({}),
  },
  showChildSubsidyApplications: {
    type: Boolean,
    default: false,
  },
  value: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits([
  'cancel',
  'change:fields',
  'change:questions',
  'edit',
  'archived',
  'save',
]);

const { api: Api, scopedApi } = useApi();

const attachments = ref([]);
const changed = ref(false);
const confirmDialog = ref(null);
const expansionPanel = ref(props.isExpandable ? [] : ['all']);
const fundingSources = ref([]);
const householdMember = ref(getCopiedValue()); // TODO: Can we receive householdMember via defineModel and not use getCopiedValue?
const processing = ref(false);
const creatingSubsidy = ref(false);
const projectedEligibility = ref(false);
const eligibilityDeterminations = ref([]);
const managedChildSubsidy = ref({});
const managedSubsidyProgramEligibilityDetermination = ref({});
const otherSubsidyProgramEligibilityDetermination = ref({});
const subsidies = ref([]);
const childSubsidyApplications = ref([]);
const loadedChildSubsidyApplicationsKey = ref(null);

const subsidyApi = computed(() => {
  return scopedApi?.subsidy || Api.parent.subsidy;
});

const householdMemberApi = computed(() => {
  return props.householdMemberApi || scopedApi?.householdMember || Api.parent.householdMember;
});

const attachmentTagsSupplements = computed(() => {
  if (householdMember.value.household_member_id) {
    return [householdMember.value.id, householdMember.value.household_member_id];
  } else {
    return [householdMember.value.id];
  }
});

const eligibilityIcon = computed(() => {
  return projectedEligibility.value ? 'check_circle' : 'cancel';
});

const eligibilityIconColor = computed(() => {
  return projectedEligibility.value ? 'green-500' : 'red';
});

const enableRelatedSubsidies = computed(() => {
  return householdMember.value.dob && props.managedSubsidyProgramId;
});

const childSubsidyApplicationsEnabled = computed(() => {
  const profile = store.state.profile || {};
  const hasOrgSubsidiesPermission = profile.org_subsidies_access || profile.org_subsidies_agent;

  return (
    props.showChildSubsidyApplications &&
    store.state.role === 'specialist' &&
    hasOrgSubsidiesPermission
  );
});

const canEditQuestions = computed(() => familySubsidiesEdit);

function cancel() {
  householdMember.value = getCopiedValue();
  emit('cancel', householdMember.value);
}

function getHouseholdMemberQuestions(householdMember) {
  if (Object.keys(props.householdMemberQuestionRegister).length === 0) return [];
  const questions = props.householdMemberQuestionRegister[householdMember.id];
  return questions;
}

function getName(householdMember) {
  const { first_name: firstName, middle_name: middleName, last_name: lastName } = householdMember;

  return [firstName, middleName, lastName].join(' ');
}

function handleChange(_event) {
  changed.value = true;
  emit('change:questions', householdMember.value);
}

function householdMemberUpdated(newVal) {
  householdMember.value = newVal;
}

function getCopiedValue() {
  return JSON.parse(JSON.stringify(props.value));
}

async function archive() {
  if (props.staged) return;

  const actionVerb = props.archiveLabel.toLowerCase();
  const confirmed = await confirmDialog.value.confirmWithText(
    `Are you sure you want to ${actionVerb} this household member?`,
    {
      title: `${props.archiveLabel} household member`,
      confirmButtonText: props.archiveLabel,
      saveButtonColor: 'red',
    },
  );
  if (!confirmed) return;

  const resp = await householdMemberApi.value.archive(householdMember.value.id);
  if (resp?.status === 200) {
    emit('archived', householdMember.value);
  }
}

async function update() {
  if (props.staged) emit('save', householdMember.value);
  if (props.preventUpdate) return emit('change:fields', householdMember.value);

  processing.value = true;
  const resp = await householdMemberApi.value
    .update(householdMember.value, props.subsidy?.id)
    .catch((error) => eventBus.error(error));
  processing.value = false;
  if (resp?.status !== 200) return null;

  emit('save', householdMember.value);
  householdMember.value = resp.data;
  changed.value = false;
  return emit('change:fields', householdMember.value);
}

async function loadAttachments() {
  if (!props.subsidy?.id) return;

  processing.value = true;
  const params = {
    owner_type: 'FamilySubsidy',
    owner_id: props.subsidy.id,
  };

  const resp = await Api.member.attachment.index(params);
  processing.value = false;

  if (resp?.status === 200) attachments.value = resp.data;
}

async function loadSubsidyProgramsAndEligibility() {
  if (!enableRelatedSubsidies.value) return;

  const subsidyParams = { household_member_id: householdMember.value.id };

  const subsidiesResponse =
    store.state.role === 'parent'
      ? await subsidyApi.value.index(store.state.profile?.group_id, subsidyParams)
      : await subsidyApi.value.index(subsidyParams);

  if (subsidiesResponse.data) subsidies.value = subsidiesResponse.data;
  managedChildSubsidy.value = subsidyForOwnerId(props.managedSubsidyProgramId);

  const eligibilitiesResponse = await Api.publicApi.eligibilityDeterminations.create({
    all_open_subsidy_programs: true,
    application_data: {
      family_data: props.subsidy,
      individual_data: householdMember.value,
    },
  });
  if (!eligibilitiesResponse?.data) return;

  eligibilityDeterminations.value = eligibilitiesResponse.data;
  managedSubsidyProgramEligibilityDetermination.value = eligibilityDeterminations.value.find(
    (result) => result.owner_id === props.managedSubsidyProgramId,
  );
  otherSubsidyProgramEligibilityDetermination.value = eligibilityDeterminations.value.filter(
    (result) => result.owner_id !== props.managedSubsidyProgramId,
  );
  if (managedSubsidyProgramEligibilityDetermination.value) {
    projectedEligibility.value =
      managedSubsidyProgramEligibilityDetermination.value.projected_eligibility;
    fundingSources.value =
      managedSubsidyProgramEligibilityDetermination.value.eligibility_determinations;
  }
}

async function loadChildSubsidyApplications() {
  if (!childSubsidyApplicationsEnabled.value || !householdMember.value?.id) {
    childSubsidyApplications.value = [];
    loadedChildSubsidyApplicationsKey.value = null;
    return;
  }

  const fetchKey = `${householdMember.value.id}:enabled`;
  if (loadedChildSubsidyApplicationsKey.value === fetchKey) {
    return;
  }

  const response = await Api.organization.subsidy
    .index({
      canonical_household_member_id: householdMember.value.id,
      sort: 'submitted_at',
      sort_dir: 'desc',
    })
    .catch((error) => eventBus.error(error));

  childSubsidyApplications.value = response?.data || [];
  loadedChildSubsidyApplicationsKey.value = fetchKey;
}

function subsidyForOwnerId(ownerId) {
  return subsidies.value.find((subsidy) => subsidy.subsidy_program_id === ownerId);
}

async function createSubsidy(subsidyProgramId) {
  if (creatingSubsidy.value) return;
  creatingSubsidy.value = true;

  try {
    const subsidyData = {
      family_subsidy_id: props.subsidy.id,
      household_member_id: householdMember.value.id,
      subsidy_program_id: subsidyProgramId,
    };

    const subsidyResponse =
      store.state.role === 'parent'
        ? await subsidyApi.value.create(store.state.profile?.group_id, subsidyData)
        : await subsidyApi.value.create(subsidyData);

    if (!subsidyResponse?.data) return;

    await router.push({
      name: 'SubsidyShow',
      query: { tab: 'Enrollment' },
      params: { id: subsidyResponse.data.id },
    });
  } finally {
    creatingSubsidy.value = false;
  }
}

async function reviewSubsidy(subsidyId, tab = 'Enrollment') {
  await router.push({
    name: 'SubsidyShow',
    query: { tab },
    params: { id: subsidyId },
  });
}

async function reviewSubsidyForProgramId(subsidyProgramId) {
  const subsidyId = subsidyForOwnerId(subsidyProgramId).id;
  await reviewSubsidy(subsidyId, 'Enrollment');
}

async function load() {
  if (!props.isExpandable || props.hideTitle) {
    await loadChildSubsidyApplications();
  }
}

onMounted(load);

watch(
  householdMember,
  async () => {
    await loadSubsidyProgramsAndEligibility();
    await loadAttachments();
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
.household-member-card {
  :deep(.v-expansion-panel-title__overlay) {
    opacity: 0 !important;
  }

  :deep(.household-member-card__title) {
    background-color: rgb(var(--v-theme-surface)) !important;

    &:focus:not(:focus-visible) {
      outline: none;
      outline-offset: unset;
    }
  }

  :deep(.v-expansion-panel-text__wrapper) {
    box-sizing: border-box;
    padding: 0 1.25rem 1rem 1.25rem;
  }
}
</style>
